import { IStyle, mergeStyles } from '@fluentui/react';
import { FunctionComponent, ReactNode, useEffect, useRef } from 'react';
import { useReffedState } from '../../hooks/reffed-state';

const MEASURING_INTERVAL_MS = 650;
const SHRINK_DETECTION_MARGIN = 2;

export interface IBwjSemiFlexContainerStyles {
  root?: IStyle;
  content?: IStyle;
}

export interface IBwjSemiFlexContainerProps {
  children?: ReactNode;
  pinWidth?: boolean;
  pinHeight?: boolean;
  styles?: IBwjSemiFlexContainerStyles;
}

const computeSetDimension = (actualSize: number | undefined, shrinkDetectionMargin: number) => {
  const desiredValue = (actualSize ?? 0) - shrinkDetectionMargin;
  return desiredValue > 0 ? desiredValue : 0;
}

export const BwjSemiFlexContainer: FunctionComponent<IBwjSemiFlexContainerProps> = (props) => {
  const { pinWidth, pinHeight } = props;
  const guideRef = useRef<HTMLInputElement>(null);
  const intervalRef = useRef(0);
  const [containerFixedWidth, setContainerFixedWidth, containerFixedWidthRef] = useReffedState(0);
  const [containerFixedHeight, setContainerFixedHeight, containerFixedHeightRef] = useReffedState(0);

  const useWidth = pinWidth === true;
  const useHeight = pinHeight === true;
  
  useEffect(() => {
    if(guideRef.current === null || (useWidth === false && useHeight === false)) { return; }

    if(useWidth) {
      const ctrFixedWidth = computeSetDimension(guideRef.current.clientWidth, SHRINK_DETECTION_MARGIN);
      setContainerFixedWidth(ctrFixedWidth);
    }
    if(useHeight) {
      const ctrFixedHeight = computeSetDimension(guideRef.current.clientHeight, SHRINK_DETECTION_MARGIN);
      setContainerFixedHeight(ctrFixedHeight);
    }

    intervalRef.current = window.setInterval(() => {
      if(guideRef.current === null) { return; }

      const guide = guideRef.current;
      if(useWidth) {
        const prevFixedWidth = containerFixedWidthRef.current;
        const guideWidth = guide.clientWidth;
        const currFixedWidth = computeSetDimension(guideWidth, SHRINK_DETECTION_MARGIN);
        if(currFixedWidth !== prevFixedWidth) {
          if(guideWidth <= prevFixedWidth) {
            setContainerFixedWidth(0);
          } else {
            setContainerFixedWidth(currFixedWidth);
          }
        }
      }

      if(useHeight) {
        const prevFixedHeight = containerFixedHeightRef.current;
        const guideHeight = guide.clientHeight;
        const currFixedHeight = computeSetDimension(guideHeight, SHRINK_DETECTION_MARGIN);
        if(currFixedHeight !== prevFixedHeight) {
          if(guideHeight <= prevFixedHeight) {
            setContainerFixedHeight(0);
          } else {
            setContainerFixedHeight(currFixedHeight);
          }
        }
      }
      
    }, MEASURING_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalRef.current);
    };
  }, [useWidth, useHeight,
    // only here for the compiler:
    containerFixedWidthRef, setContainerFixedWidth,
    containerFixedHeightRef, setContainerFixedHeight
  ]);

  // these are only for scenarios where a pinned container dimension is 0, 
  // because when a shrink occurs, we set the dimension to 0 to give the DOM an
  // opportunity to figure out what the dimension should be without being influenced by the
  // hard set value.  After having been given this chance, we want to pop the container back out to the desired dimension
  useEffect(() => {
    if(containerFixedWidth !== 0 || !useWidth) { return; }
    
    window.setTimeout(()=>{
      const guide = guideRef.current as HTMLInputElement;
      const guideWidth = guide.clientWidth;

      const fixedWidth = computeSetDimension(guideWidth, SHRINK_DETECTION_MARGIN);
      setContainerFixedWidth(fixedWidth);
    }, 10);
  }, [containerFixedWidth, useWidth,
    // here for compiler:
    setContainerFixedWidth
  ]);

  useEffect(() => {
    if(containerFixedHeight !== 0 || !useHeight) { return; }
    
    window.setTimeout(()=>{
      const guide = guideRef.current as HTMLInputElement;
      const guideHeight = guide.clientHeight;

      const fixedHeight = computeSetDimension(guideHeight, SHRINK_DETECTION_MARGIN);
      setContainerFixedHeight(fixedHeight);
    }, 10);
  }, [containerFixedHeight, useHeight,
    // for compiler:
    setContainerFixedHeight
  ]);

  const rootClass = !!props.styles?.root ? mergeStyles(props.styles.root) : undefined;
  const contentClass = !!props.styles?.content ? mergeStyles(props.styles.content) : undefined;

  return (
    <div ref={guideRef} className={rootClass}>
      <div
        style={{ width: useWidth ? containerFixedWidth : undefined, height: useHeight ? containerFixedHeight : undefined }}
        className={contentClass}
      >
        {props.children}
      </div>
    </div>
  );
};